import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Trash } from 'lucide-react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { QuestionTitleInput } from './QuestionTitleInput';
import { QuestionTypeSelector } from './QuestionTypeSelector';

interface TableColumnEditorProps {
  column: PolicyFormTableColumn;
  onChange: (updated: PolicyFormTableColumn) => void;
  onRemove?: () => void;
}

export const TableColumnEditor: React.FC<TableColumnEditorProps> = ({
  column,
  onChange,
  onRemove,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [newOption, setNewOption] = useState<{ id: string; value: string }>({
    id: '',
    value: '',
  });

  const handleOptionChange = (idx: number, updates: { id?: string; value?: string }) => {
    const updatedOptions = [...(column.options || [])];
    updatedOptions[idx] = { ...updatedOptions[idx], ...updates };
    onChange({ ...column, options: updatedOptions });
  };

  const handleRemoveOption = (idx: number) => {
    const updatedOptions = [...(column.options || [])];
    updatedOptions.splice(idx, 1);
    onChange({ ...column, options: updatedOptions });
  };

  const handleAddOption = () => {
    if (!newOption.id || !newOption.value) return;
    const updatedOptions = [...(column.options || []), newOption];
    onChange({ ...column, options: updatedOptions });
    setNewOption({ id: '', value: '' });
  };

  return (
    <div className="flex flex-col space-y-2 border-b px-1 py-2 hover:bg-gray-50">
      {/* Header Row */}
      <div className="flex items-center gap-3">
        <QuestionTitleInput
          questionId={column.id}
          value={column.title}
          onChange={(id, updates) => {
            if (typeof updates.title === 'string') {
              onChange({ ...column, title: updates.title });
            }
          }}
        />

        <QuestionTypeSelector
          questionId={column.id}
          value={column.type}
          onChange={(id, updates) => {
            const allowedTypes: PolicyFormTableColumn['type'][] = [
              'text',
              'number',
              'date',
              'radio',
              'select',
              'textselect',
            ];

            const type = updates.type as PolicyFormTableColumn['type'];
            if (!allowedTypes.includes(type)) return;

            // Only extract the type property, ignore other properties from PolicyFormQuestion
            // that may not be compatible with PolicyFormTableColumn (e.g., validationType has fewer options)
            onChange({ ...column, type });
          }}
        />

        {['select', 'multiselect', 'radio'].includes(column.type) && (
          <button
            className="mr-2 flex items-center gap-1 text-xs text-blue-600"
            onClick={() => setShowOptions(prev => !prev)}
          >
            {showOptions ? (
              <>
                <ChevronUp size={14} /> Hide Options
              </>
            ) : (
              <>
                <ChevronDown size={14} /> View Options
              </>
            )}
          </button>
        )}

        <button
          type="button"
          onClick={() => onChange({ ...column, required: !column.required })}
          className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors
          ${
            column.required
              ? 'bg-red-500 text-white'
              : 'border border-red-500 bg-white text-red-500'
          }`}
          title={column.required ? 'Required' : 'Not Required'}
        >
          R
        </button>

        {onRemove && (
          <Button
            size="sm"
            variant="destructive"
            onClick={onRemove}
            className="ml-auto"
          >
            <Trash size={14} />
          </Button>
        )}
      </div>

      {/* Options Row */}
      {showOptions && ['select', 'multiselect', 'radio'].includes(column.type) && (
        <div className="ml-6 space-y-2">
          {column.options?.map((opt, idx) => (
            <div
              key={opt.id}
              className="flex items-center gap-2"
            >
              <input
                value={opt.id}
                onChange={e => handleOptionChange(idx, { id: e.target.value })}
                placeholder="Option ID"
                className="w-36 rounded border px-2 py-1 text-xs"
              />
              <input
                value={opt.value}
                onChange={e => handleOptionChange(idx, { value: e.target.value })}
                placeholder="Option Value"
                className="flex-1 rounded border px-2 py-1 text-sm"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleRemoveOption(idx)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash size={14} />
              </Button>
            </div>
          ))}

          {/* Add new option */}
          <div className="mt-1 flex items-center gap-2">
            <input
              type="text"
              value={newOption.id}
              onChange={e => setNewOption(prev => ({ ...prev, id: e.target.value }))}
              placeholder="New Option ID"
              className="w-36 rounded border px-2 py-1 text-xs"
            />
            <input
              type="text"
              value={newOption.value}
              onChange={e => setNewOption(prev => ({ ...prev, value: e.target.value }))}
              placeholder="New Option Value"
              className="flex-1 rounded border px-2 py-1 text-sm"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddOption}
              className="text-green-600 hover:text-green-700"
            >
              + Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
